import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { QrJobApi } from './api';
import type { CreateQrJobRequest, QrJob, QrJobItemParams } from './types';

const POLL_INTERVAL_MS = 2000;

const QUERY_KEYS = {
  all: ['qr-jobs'] as const,
  detail: (orgId: string, jobId: string) => [...QUERY_KEYS.all, orgId, 'detail', jobId] as const,
  items: (orgId: string, jobId: string, params: QrJobItemParams, processed: number) =>
    [...QUERY_KEYS.all, orgId, 'items', jobId, params, processed] as const,
};

const isRunning = (job?: QrJob): boolean => job?.status === 'running';

export const useQrJob = (orgId: string, jobId: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.detail(orgId, jobId),
    queryFn: () => QrJobApi.get(orgId, jobId),
    enabled: !!orgId && !!jobId,
    refetchInterval: (query) => (isRunning(query.state.data) ? POLL_INTERVAL_MS : false),
  });
};

/**
 * Keyed on the job's processed count (succeeded + failed) from useQrJob, so the page refetches
 * exactly when a batch lands — including the last one — without polling on its own.
 */
export const useQrJobItems = (orgId: string, jobId: string, params: QrJobItemParams, processed: number) => {
  return useQuery({
    queryKey: QUERY_KEYS.items(orgId, jobId, params, processed),
    queryFn: () => QrJobApi.listItems(orgId, jobId, params),
    enabled: !!orgId && !!jobId,
    placeholderData: keepPreviousData,
  });
};

export const useCreateQrJob = (orgId: string) => {
  return useMutation({
    mutationFn: (data: CreateQrJobRequest) => QrJobApi.create(orgId, data),
  });
};

export { QUERY_KEYS as qrJobQueryKeys };
